import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { 
  Headphones, BookOpen, PenTool, Mic, CheckCircle, Trophy, 
  BarChart3, Zap, Shield, Star, ArrowRight, Sparkles,
  Award, GraduationCap, Globe, Rocket
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (user && !loading) {
      if (user.selected_level) {
        navigate(`/level/${user.selected_level}`);
      } else {
        navigate('/levels');
      }
    }
  }, [user, loading, navigate]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/levels';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { icon: Headphones, title: "Compréhension Orale", desc: "Master listening with real DELF audio formats", color: "bg-blue-500" },
    { icon: BookOpen, title: "Compréhension Écrite", desc: "Strategic reading practice with AI guidance", color: "bg-green-500" },
    { icon: PenTool, title: "Production Écrite", desc: "Writing exercises with personalized AI feedback", color: "bg-purple-500" },
    { icon: Mic, title: "Production Orale", desc: "Speaking practice with pronunciation analysis", color: "bg-orange-500" },
  ];

  const benefits = [
    { icon: Trophy, title: "Mastery-Based System", desc: "Progress only when you're truly ready" },
    { icon: BarChart3, title: "AI-Powered Analytics", desc: "Track weaknesses and improve faster" },
    { icon: Zap, title: "Daily Practice Mode", desc: "Build habits with 5-minute exercises" },
    { icon: Shield, title: "Exam Simulation", desc: "Real test conditions before the exam" },
  ];

  const levels = [
    { level: "A2", name: "Elementary", color: "from-green-400 to-green-600" },
    { level: "B1", name: "Intermediate", color: "from-blue-400 to-blue-600" },
    { level: "B2", name: "Advanced", color: "from-purple-400 to-purple-600" },
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FDFCF8]/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
              DELF Success Academy
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href="mailto:support@delfsuccessacademy.com" className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:inline" data-testid="contact-support-header">
              Contact Support
            </a>
            <Link to="/pricing">
              <Button variant="ghost" className="font-medium">Pricing</Button>
            </Link>
            <Button onClick={handleLogin} className="rounded-full px-6 font-semibold" data-testid="login-btn">
              Se connecter
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center gap-2 mb-6">
              {levels.map((l) => (
                <span key={l.level} className={`px-3 py-1 rounded-full text-sm font-bold text-white bg-gradient-to-r ${l.color}`}>
                  {l.level}
                </span>
              ))}
            </div>
            
            <h1 
              className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-none mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              Réussir DELF<br />
              <span className="text-primary">A2 • B1 • B2</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl mx-auto">
              Your complete system to pass the DELF exam with confidence. 
              Master all 4 modules with AI-powered feedback and structured practice.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/quiz')}
                size="lg"
                className="rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all gap-2"
                data-testid="find-level-btn"
              >
                <Zap className="w-5 h-5" />
                Find My Level (Free)
              </Button>
              <Button
                onClick={() => navigate('/levels')}
                variant="outline"
                size="lg"
                className="rounded-full px-8 py-6 text-lg font-medium"
                data-testid="choose-level-btn"
              >
                Choose My Level
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            
            {/* Free Trial Banner */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-green-50 border border-green-200 rounded-full"
            >
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800 font-medium">
                Try before you buy – Experience the DELF system for free
              </span>
            </motion.div>
            
            <div className="mt-6 flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>4 complete modules</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>40 exercises per level</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>AI feedback</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Master All 4 DELF Modules
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Complete preparation covering every aspect of the DELF exam
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#FDFCF8] rounded-2xl p-6 border border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300"
              >
                <div className={`w-14 h-14 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                  <feature.icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Authority Section - Coach Introduction */}
      <section className="py-20 px-6 bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Learn from a DELF Expert
            </h2>
            <p className="text-lg text-muted-foreground">
              Your success is guided by decades of teaching excellence
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-border/50"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shrink-0">
                <GraduationCap className="w-16 h-16 md:w-20 md:h-20 text-white" />
              </div>
              
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Tony Anicet Eboulondzi
                </h3>
                <p className="text-primary font-medium mb-2">Member of the Examination Jury of DELF Exams</p>
                <p className="text-sm text-muted-foreground mb-4">25+ years of experience preparing students for DELF A2, B1, and B2 exams with proven success.</p>
                
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-3 bg-primary/5 rounded-xl">
                    <div className="text-2xl font-bold text-primary">25+</div>
                    <div className="text-xs text-muted-foreground">Years Teaching</div>
                  </div>
                  <div className="text-center p-3 bg-primary/5 rounded-xl">
                    <div className="text-2xl font-bold text-primary">2,500+</div>
                    <div className="text-xs text-muted-foreground">Students Prepared</div>
                  </div>
                  <div className="text-center p-3 bg-primary/5 rounded-xl">
                    <div className="text-2xl font-bold text-primary">95%</div>
                    <div className="text-xs text-muted-foreground">Pass Rate</div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-1">
                    <Award className="w-4 h-4" /> Certified DELF Examiner
                  </span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium flex items-center gap-1">
                    <Globe className="w-4 h-4" /> Alliance Française
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                Why DELF Success Academy?
              </h2>
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <benefit.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">{benefit.title}</h3>
                      <p className="text-muted-foreground text-sm">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-3xl p-8 border border-primary/20">
                <div className="space-y-4">
                  {levels.map((l, i) => (
                    <div key={l.level} className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${l.color} flex items-center justify-center text-white font-bold`}>
                        {l.level}
                      </div>
                      <div className="flex-1">
                        <div className="font-bold">DELF {l.level}</div>
                        <div className="text-sm text-muted-foreground">{l.name} • 4 modules</div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">40</div>
                        <div className="text-xs text-muted-foreground">exercises</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <p className="text-xl font-medium mb-2">"The best DELF preparation I've ever used"</p>
          <p className="text-muted-foreground">Join thousands of successful DELF candidates</p>
          <div className="flex items-center justify-center gap-8 mt-8">
            <div>
              <div className="text-3xl font-bold text-primary">2,500+</div>
              <div className="text-sm text-muted-foreground">Successfully Prepared</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary">95%</div>
              <div className="text-sm text-muted-foreground">Pass Rate</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary">4.9/5</div>
              <div className="text-sm text-muted-foreground">Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STUDENT SUCCESS STORIES ===== */}
      <section className="py-20 px-6 bg-[#FDFCF8]" data-testid="student-success-stories-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 text-amber-700 text-sm font-medium mb-5 border border-amber-200">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              Verified Student Results
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Real Stories, Real Results
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Hear from students around the world who passed their DELF exams with DELF Success Academy
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
            {[
              { name: 'Amina', country: 'Morocco', flag: '🇲🇦', level: 'B2', score: '91/100', quote: 'The structured approach helped me focus on my weak points. The AI corrections for writing were incredibly detailed. I passed B2 on my first attempt!', photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&fit=crop&crop=face' },
              { name: 'Lucas', country: 'Brazil', flag: '🇧🇷', level: 'B1', score: '87/100', quote: 'I only had 60 days to prepare. The personalized study plan kept me on track every day. Listening exercises were exactly like the real exam.', photo: 'https://images.unsplash.com/photo-1633112639964-f8c9d360dc75?w=120&h=120&fit=crop&crop=face' },
              { name: 'Mei-Lin', country: 'Japan', flag: '🇯🇵', level: 'A2', score: '96/100', quote: 'As a complete beginner, I was nervous. The step-by-step exercises built my confidence. The speaking practice with AI feedback was a game changer.', photo: 'https://images.unsplash.com/photo-1619431667975-e93b820cde63?w=120&h=120&fit=crop&crop=face' },
              { name: 'Sofia', country: 'Colombia', flag: '🇨🇴', level: 'B2', score: '89/100', quote: 'The writing module taught me exactly how the examiners grade. My production écrite score went from 12 to 22 out of 25. Absolutely worth it.', photo: 'https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?w=120&h=120&fit=crop&crop=face' },
              { name: 'Emma', country: 'United Kingdom', flag: '🇬🇧', level: 'B1', score: '92/100', quote: 'I tried other apps but nothing was DELF-specific. This platform understands the exam format perfectly. The reading strategies saved me so much time.', photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=120&h=120&fit=crop&crop=face' },
              { name: 'Kévin', country: 'Congo', flag: '🇨🇩', level: 'A2', score: '78/100', quote: 'Studying from Kinshasa, I had no access to a French teacher. This platform gave me everything I needed. I passed A2 with 78/100!', photo: 'https://images.unsplash.com/photo-1517256673644-36ad11246d21?w=120&h=120&fit=crop&crop=face' }
            ].map((t, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="bg-white rounded-2xl p-6 border border-border/50 hover:shadow-lg hover:border-primary/20 transition-all duration-300 group"
                data-testid={`success-story-${index}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={t.photo}
                    alt={t.name}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-primary/10 shadow-sm"
                    loading="lazy"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-sm">{t.name}</span>
                      <span className="text-xs text-muted-foreground">{t.flag} {t.country}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">DELF {t.level}</span>
                      <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">{t.score}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-0.5 mb-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>

                <p className="text-sm text-gray-600 leading-relaxed">
                  "{t.quote}"
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
            data-testid="success-stories-cta"
          >
            <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-5" style={{ fontFamily: 'Syne, sans-serif' }}>
              Ready to Become Our Next Success Story?
            </h3>
            <Button
              size="lg"
              className="rounded-full px-10 py-6 font-bold text-[15px] shadow-lg shadow-primary/25"
              onClick={() => navigate('/pricing')}
              data-testid="success-stories-cta-btn"
            >
              <Rocket className="w-5 h-5 mr-2" />
              Start My DELF Journey Now
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Ready to pass your DELF exam?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start your preparation today with a free level assessment
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/quiz')}
                size="lg"
                className="rounded-full px-10 py-6 text-lg font-bold shadow-lg"
              >
                Take Free Assessment
              </Button>
              <Button
                onClick={() => navigate('/pricing')}
                variant="outline"
                size="lg"
                className="rounded-full px-10 py-6 text-lg font-medium"
              >
                View Pricing
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border bg-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="font-semibold">DELF Success Academy</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Need help? Contact us at{' '}
            <a href="mailto:support@delfsuccessacademy.com" className="text-primary hover:underline" data-testid="contact-support-footer">
              support@delfsuccessacademy.com
            </a>
          </p>
          <p className="text-sm text-muted-foreground">
            &copy; 2026 DELF Success Academy. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
